import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/YubuMarketWhite - 1024x206.png';
import { useAuth } from '../contexts/AuthContext';
import { useShop } from '../contexts/ShopContext';
import { useMessageUnreadCount } from '../hooks/useMessageUnreadCount';
import { useIsEffectiveBuyer, useIsEffectiveShopUser } from '../hooks/useEffectiveRole';
import { CART_UPDATED_EVENT, getCart } from '../services/cartApi';

function Navbar() {
  const { currentUser, loading, logout } = useAuth();
  const { shopDocument } = useShop();
  const navigate = useNavigate();
  const collapseRef = useRef(null);

  const closeMenu = () => {
    const el = collapseRef.current;
    if (!el) return;
    // Use Bootstrap's JS API if available, otherwise remove the class directly
    if (window.bootstrap?.Collapse) {
      window.bootstrap.Collapse.getOrCreateInstance(el).hide();
    } else {
      el.classList.remove('show');
    }
  };

  const isBuyer = useIsEffectiveBuyer();
  const isShopUser = useIsEffectiveShopUser();
  const { unreadCount } = useMessageUnreadCount({
    enabled: Boolean(currentUser && (isBuyer || isShopUser)),
    shopId: isShopUser ? shopDocument?.id : null,
  });
  const [cartCountState, setCartCountState] = useState({ user: null, count: 0 });
  const cartCount = currentUser && isBuyer && cartCountState.user === currentUser
    ? cartCountState.count
    : 0;

  useEffect(() => {
    if (!currentUser || !isBuyer) {
      return undefined;
    }

    let cancelled = false;
    const refreshCartCount = (event) => {
      if (event?.detail?.cart) {
        setCartCountState({ user: currentUser, count: event.detail.cart.totalItems || 0 });
        return;
      }
      getCart({ suppressError: true })
        .then((cart) => {
          if (!cancelled) setCartCountState({ user: currentUser, count: cart.totalItems || 0 });
        })
        .catch(() => {
          if (!cancelled) setCartCountState({ user: currentUser, count: 0 });
        });
    };

    refreshCartCount();
    window.addEventListener(CART_UPDATED_EVENT, refreshCartCount);
    return () => {
      cancelled = true;
      window.removeEventListener(CART_UPDATED_EVENT, refreshCartCount);
    };
  }, [currentUser, isBuyer]);

  const handleLogout = async () => {
    closeMenu();
    await logout();
    navigate('/');
  };

  return (
    <nav className="app-navbar navbar navbar-expand-lg navbar-dark bg-dark sticky-top shadow-sm">
      <div className="container">
        <Link className="navbar-brand d-flex align-items-center" to="/">
          <img src={logo} alt="YubuMarket" style={{ height: 36, objectFit: 'contain' }} />
        </Link>

        {/* Toggler — right-aligned on mobile */}
        <button
          className="navbar-toggler ms-auto"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarMenu"
          aria-controls="navbarMenu"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon" />
        </button>

        <div className="collapse navbar-collapse" id="navbarMenu" ref={collapseRef}>
          <ul className="navbar-nav ms-auto mb-2 mb-lg-0">
            {!loading && currentUser ? (
              isBuyer ? (
                <>
                  <li className="nav-item">
                    <Link to="/" className="nav-link" onClick={closeMenu}>
                      Home
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/shops" className="nav-link" onClick={closeMenu}>
                      Shops
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/dashboard" className="nav-link" onClick={closeMenu}>
                      Dashboard
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/cart" className="nav-link" onClick={closeMenu}>
                      Cart
                      {cartCount > 0 ? (
                        <span className="badge bg-warning text-dark ms-2">{cartCount}</span>
                      ) : null}
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/buyer/promo" className="nav-link" onClick={closeMenu}>
                      Promo
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/buyer/messages" className="nav-link" onClick={closeMenu}>
                      Messages
                      {unreadCount > 0 ? (
                        <span className="badge bg-danger ms-2">{unreadCount}</span>
                      ) : null}
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/buyer/users/profile" className="nav-link" onClick={closeMenu}>
                      Profile
                    </Link>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className="btn nav-link border-0 bg-transparent"
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </li>
                </>
              ) : (
                <>
                  <li className="nav-item">
                    <Link to="/dashboard" className="nav-link" onClick={closeMenu}>
                      Dashboard
                    </Link>
                  </li>
                  {isShopUser && (
                    <li className="nav-item">
                      <Link to="/shop/users/profile" className="nav-link" onClick={closeMenu}>
                        Profile
                      </Link>
                    </li>
                  )}
                  <li className="nav-item">
                    <button
                      type="button"
                      className="btn nav-link border-0 bg-transparent"
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </li>
                </>
              )
            ) : (
              <>
                <li className="nav-item">
                  <Link to="/" className="nav-link" onClick={closeMenu}>
                    Home
                  </Link>
                </li>
                <li className="nav-item">
                  <Link to="/shops" className="nav-link" onClick={closeMenu}>
                    Shops
                  </Link>
                </li>
                <li className="nav-item">
                  <Link to="/image-search" className="nav-link" onClick={closeMenu}>
                    Image Search
                  </Link>
                </li>
                <li className="nav-item">
                  <Link to="/cart" className="nav-link" onClick={closeMenu}>
                    Cart
                    {cartCount > 0 ? (
                      <span className="badge bg-warning text-dark ms-2">{cartCount}</span>
                    ) : null}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link to="/plans" className="nav-link" onClick={closeMenu}>
                    Plans
                  </Link>
                </li>
                <li className="nav-item">
                  <Link to="/register/buyer" className="nav-link" onClick={closeMenu}>
                    Register as Buyer
                  </Link>
                </li>
                <li className="nav-item">
                  <Link to="/register/shop" className="nav-link" onClick={closeMenu}>
                    Register as Seller
                  </Link>
                </li>
                <li className="nav-item">
                  <Link to="/login" className="nav-link" onClick={closeMenu}>
                    Log In
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
